import { ReactNode, useState } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Card } from '../../ui/card';

interface FormCardProps {
    title: string;
    description?: string;
    icon?: LucideIcon;
    aside?: ReactNode;
    defaultOpen?: boolean;
    children: ReactNode;
}

export default function FormCard({
    title,
    description,
    icon: Icon,
    aside,
    defaultOpen = true,
    children,
}: FormCardProps) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Card className="overflow-hidden border-subtle">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={cn(
                    'flex w-full items-center gap-3 bg-input px-6 py-4 text-left transition-colors hover:bg-accent',
                    open && 'border-b border-subtle'
                )}
            >
                {Icon && (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-subtle bg-accent">
                        <Icon
                            aria-hidden="true"
                            className="h-4 w-4 text-foreground"
                        />
                    </span>
                )}
                <span className="min-w-0 flex-1">
                    <span className="block text-base font-semibold tracking-tight">
                        {title}
                    </span>
                    {description && (
                        <span className="mt-0.5 block text-xs font-medium text-subtle-foreground">
                            {description}
                        </span>
                    )}
                </span>
                {aside}
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        'h-4 w-4 shrink-0 text-subtle-foreground motion-safe:transition-transform',
                        !open && '-rotate-90'
                    )}
                />
            </button>
            <div className={cn('p-6', !open && 'hidden')}>{children}</div>
        </Card>
    );
}
